import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { Balance, Recharge, Recharges } from '../model/balance.model'
import { FetchBalanceProps, FetchRechargesProps } from '../services/balance.service'

import { GrossAndNet, Period, StoreError } from '~/common/model'

interface CustomError {
  subscriptionId: string
  error: StoreError
}

interface FetchMoreRechargesProps {
  subscriptionId: string
  monthsToAdd: number
  brand: string
  sub: string
}

export type BalanceState = {
  [key: string]: SingleSubscriptionBalance
}

export type SingleSubscriptionBalance = {
  balance: {
    data?: {
      amount: GrossAndNet
    }
    loading: boolean
    error?: StoreError
  }
  recharges: {
    data?: {
      periodDate: Period
      recharges: Recharge[]
    }
    loading: boolean
    error?: StoreError
  }
}

export const initialState: BalanceState = {}

export const balanceSlice = createSlice({
  name: 'balance',
  initialState,
  reducers: {
    fetchBalance: (state, action: PayloadAction<FetchBalanceProps>) => {
      const {
        payload: { subscriptionId },
      } = action
      return {
        ...state,
        [subscriptionId]: { ...state[subscriptionId], balance: { loading: true } },
      }
    },
    fetchBalanceSuccess(state, action: PayloadAction<Balance>) {
      const {
        payload: { subscriptionId, amount },
      } = action

      return {
        ...state,
        [subscriptionId]: {
          ...state[subscriptionId],
          balance: { data: { amount }, loading: false },
        },
      }
    },
    fetchBalanceError(state, action: PayloadAction<CustomError>) {
      const {
        payload: { subscriptionId, error },
      } = action
      return {
        ...state,
        [subscriptionId]: { ...state[subscriptionId], balance: { loading: false, error } },
      }
    },
    fetchRecharges: (state, action: PayloadAction<FetchRechargesProps>) => {
      const {
        payload: { subscriptionId },
      } = action
      return {
        ...state,
        [subscriptionId]: {
          ...state[subscriptionId],
          recharges: { loading: true },
        },
      }
    },
    fetchRechargesSuccess(state, action: PayloadAction<Recharges>) {
      const {
        payload: { subscriptionId, recharges, periodDate },
      } = action

      return {
        ...state,
        [subscriptionId]: {
          ...state[subscriptionId],
          recharges: {
            data: { periodDate, recharges },
            loading: false,
          },
        },
      }
    },
    fetchMoreRecharges: (state, action: PayloadAction<FetchMoreRechargesProps>) => {
      const {
        payload: { subscriptionId },
      } = action
      return {
        ...state,
        [subscriptionId]: {
          ...state[subscriptionId],
          recharges: { ...state[subscriptionId].recharges, loading: true },
        },
      }
    },
    fetchMoreRechargesSuccess: (state, action: PayloadAction<Recharges>) => {
      const {
        payload: { subscriptionId, recharges, periodDate },
      } = action

      return {
        ...state,
        [subscriptionId]: {
          ...state[subscriptionId],
          recharges: {
            data: {
              periodDate: {
                endDate: state[subscriptionId].recharges?.data?.periodDate.endDate || '',
                startDate: periodDate.startDate,
              },
              recharges: [...(state[subscriptionId].recharges.data?.recharges || []), ...recharges],
            },
            loading: false,
          },
        },
      }
    },
    fetchRechargesError(state, action: PayloadAction<CustomError>) {
      const {
        payload: { subscriptionId, error },
      } = action
      return {
        ...state,
        [subscriptionId]: {
          ...state[subscriptionId],
          recharges: {
            ...state[subscriptionId].recharges,
            error,
            loading: false,
          },
        },
      }
    },
  },
})

export const {
  fetchBalance,
  fetchBalanceSuccess,
  fetchBalanceError,
  fetchRecharges,
  fetchMoreRecharges,
  fetchMoreRechargesSuccess,
  fetchRechargesSuccess,
  fetchRechargesError,
} = balanceSlice.actions

const balanceReducer = balanceSlice.reducer
export default balanceReducer
